/**
 * Employee Web — `/me/documents` tab route (EW-05 self-documents read).
 *
 * Phase 03 Plan 03-12b Task 1. Consumes the shared `<DocumentList>` DS
 * pattern (lifted in plan 03-12a) — upload affordance omitted per D-19
 * — EW employees do not self-upload; HR admins upload on their behalf.
 * Download + PDF inline preview work identically to HR Admin.
 */
import { useState, type JSX } from "react";
import { humanizeCode } from "@linkhr/hr-domain";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "@linkhr/i18n";
import {
  Card,
  DocumentList,
  PageHeader,
  PdfPreview,
  nonEmpty,
  type DocumentListDocument,
  type DocumentListLabels,
  type NonEmptyString,
} from "@linkhr/design-system";
import { useMeDocumentList, useMeEmployee } from "@linkhr/api-client";
import { MyCompanyDocumentsSection } from "../features/me/documents/MyCompanyDocumentsSection";

/**
 * Build a self-documents download URL. EW streams through the same shaped
 * endpoint as HR Admin — the backend dispatches based on the JWT sub claim
 * (plan 03-12a handler). URL is relative; `<a href>` navigation skips the
 * api-client middleware chain.
 */
function buildMeDocumentDownloadUrl(documentId: string): string {
  return `/api/v1/me/documents/${encodeURIComponent(documentId)}/download`;
}

export const Route = createFileRoute("/_auth/me/documents")({
  staticData: { breadcrumb: { labelKey: "me-profile:tabs.documents" } },
  component: DocumentsTabRoute,
});

function DocumentsTabRoute(): JSX.Element | null {
  const { t, i18n } = useTranslation(["me-profile", "documents", "common"]);
  const meQuery = useMeEmployee();
  const { data: docs = [], isPending } = useMeDocumentList();
  const [preview, setPreview] = useState<DocumentListDocument | null>(null);

  const me = meQuery.data;
  if (!me) return null;

  // Pre-localized labels bundle for the DS pattern — matches the HR Admin
  // shim's convention (the DS pattern is i18next-free; feature route owns
  // the translation bridge). See plan 03-12a key-decisions.
  const labels: DocumentListLabels = {
    emptyHeading: t("me-profile:documents.empty_heading", {
      defaultValue: "Your documents will appear here",
    }),
    emptyBody: t("me-profile:documents.empty_body", {
      defaultValue: "Once HR uploads your documents, you can view and download them here.",
    }),
    emptyEntities: nonEmpty(
      t("me-profile:documents.empty_heading", {
        defaultValue: "documents",
      }),
    ) as NonEmptyString,
    emptyNextAction: nonEmpty(
      t("me-profile:documents.empty_body", {
        defaultValue: "wait for HR to upload",
      }),
    ) as NonEmptyString,
    loadingAria: nonEmpty(
      t("common:loading.generic", { defaultValue: "Loading…" }),
    ) as NonEmptyString,
    // A document type the company owns may have no bundled label, and the dev
    // missing-key handler throws whatever default is passed — humanize instead.
    typeLabelFor: (type) =>
      i18n.exists(`documents:types.${type}`) ? t(`documents:types.${type}`) : humanizeCode(type),
    daysLeftFor: (days) =>
      t("documents:expiry.days_left", {
        days,
        defaultValue: `${days.toString()} days left`,
      }),
    expiredLabel: t("documents:expiry.expired", {
      defaultValue: "Expired",
    }),
    previewAria: nonEmpty(
      t("documents:action.preview", { defaultValue: "Preview" }),
    ) as NonEmptyString,
    downloadAria: t("documents:action.download", {
      defaultValue: "Download",
    }),
    // DocumentList requires this label even when `onDelete` is omitted;
    // keep it present so the type shape stays complete. EW never renders
    // the delete button (no `onDelete` supplied below).
    deleteAria: nonEmpty(
      t("documents:action.delete", { defaultValue: "Delete" }),
    ) as NonEmptyString,
  };

  // Adapter: backend `EmployeeDocument` (CC-07 / DocumentResponse) →
  // DocumentList shape. Same shape as the HR Admin DocumentsTabContent
  // adapter — keeps the DS pattern stable while hooks emit the canonical
  // backend shape.
  const docsForList: ReadonlyArray<DocumentListDocument> = docs.map(
    (d): DocumentListDocument => ({
      id: d.id,
      type: d.docType,
      fileName: d.titleEn ?? d.titleAr ?? d.fileName ?? d.id,
      mimeType: d.contentType,
      uploadedAt: d.uploadedAt,
      expiresAt: d.expiryDate,
    }),
  );

  return (
    <section className="flex flex-col gap-lg">
      <PageHeader
        title={t("me-profile:documents.page_title")}
        subtitle={t("me-profile:documents.page_subtitle")}
      />
      <Card>
        <DocumentList
          documents={docsForList}
          isPending={isPending}
          onPreview={(doc) => {
            if (doc.mimeType === "application/pdf") setPreview(doc);
          }}
          buildDownloadUrl={buildMeDocumentDownloadUrl}
          labels={labels}
        />
      </Card>
      <MyCompanyDocumentsSection />
      {preview !== null && (
        <PdfPreview
          open
          onOpenChange={(o) => {
            if (!o) setPreview(null);
          }}
          fileUrl={buildMeDocumentDownloadUrl(preview.id)}
          titleKey={nonEmpty(preview.fileName) as NonEmptyString}
        />
      )}
    </section>
  );
}
